import { useState, useEffect } from "react";
import { getBasicAuthHeader } from "../../Utils/getBasicAuthHeader";

/**
 * Fetches data from the api.
 *
 * @param {function(object)} onSuccess
 * @param {function(object)} onError
 *
 * @returns {AbortController}
 */
function fetchData(endpoint, onSuccess, onError) {
    console.log('Fetching data from endpoint:', endpoint);
    // Create an abort controller
    const controller = new AbortController();

    const authorization = getBasicAuthHeader();

    const settings = {
        method: 'GET',
        credentials: window.includeApiCredentials ? 'include' : 'same-origin',
        signal: controller.signal,
        headers: authorization ? {
            'Authorization': authorization,
        } : {}
    }

    // Catch errors, but not AbortErrors
    function catchError(error) {
        if (error.name !== 'AbortError') {
            onError(error);
        }
    }

    // Fetch from API
    fetch(endpoint, settings)
        .then(response => response.json())
        .then(onSuccess)
        .catch(catchError);

    // Return controller to allow abort
    return controller;
}

/**
 * Content hook.
 *
 * @returns {object}
 */
function useContent(endpoint) {
    // Set loading, error, and results state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState();
    const [content, setContent] = useState();

    // Set up an effect to react to query changes.
    useEffect(() => {
        // Set loading state
        setLoading(true);

        // Use fetchData function to get a fetch controller for abort handling
        const controller = fetchData(endpoint, (data) => {
            // Handle success
            setLoading(false);
            setError(undefined);
            setContent(data);
        }, (error) => {
            // Handle error
            setLoading(false);
            setError(error);
            setContent(undefined);
        });

        // Return controller abort as effect cleanup to abort pending requests if the query changes rapidly
        return () => controller.abort();
    }, [endpoint]);

    return {
        content,
        loading,
        error,
    };
}

export default useContent;
